import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Cpu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import {
  updateFileController,
  reorderFile,
  setOrderConfirmed,
  selectV2,
} from '../v2Upload.slice';
import FileCard from './FileCard';
import GcodeDetailModal from './GcodeDetailModal';
import ControllerLogo from './ControllerLogo';
import { SectionPanel, type SectionAddProps } from './FileGrids';
import { NoGcodeArt } from './EmptySectionArt';
import { dropBeforeId } from '../utils/cutOrder';
import { InsertBarSlot as SharedInsertBarSlot } from './InsertBar';
import { V2File } from '../v2Upload.d';

const ORDER_LABEL =
  "I've arranged these files in the order they should be carved";

const CONTROLLER_DEFS = [
  { key: 'Redline', sub: 'accepts .nc, .tap' },
  { key: 'Masso', sub: 'accepts .gcode, .nc' },
  { key: 'Buildbotics', sub: 'accepts .gcode, .ngc' },
];

/** The carve-order sign-off. Continue is shut until it is ticked, so the
 * parser's guessed order is never shipped without a human agreeing to it. */
function OrderGate({ count }: { count: number }) {
  const dispatch = useDispatch();
  const { orderConfirmed } = useSelector(selectV2);
  if (count === 0) return null;
  return (
    <label
      htmlFor="v2-order-confirmed"
      data-focus="order-confirmed"
      className={cn(
        'flex items-center gap-2.5 rounded-lg border bg-white px-3.5 py-2 text-sm font-medium cursor-pointer transition-colors',
        orderConfirmed
          ? 'border-[#22C55E] text-green-700'
          : 'border-destructive/60 text-heading'
      )}
    >
      <Checkbox
        id="v2-order-confirmed"
        checked={orderConfirmed}
        onCheckedChange={(checked) =>
          dispatch(setOrderConfirmed(checked === true))
        }
        className={cn(
          // rounded-sm is 8px here, which reads as a circle at this size
          'h-[18px] w-[18px] rounded-[4px]',
          !orderConfirmed && 'border-destructive'
        )}
      />
      <span className="leading-snug">{ORDER_LABEL}</span>
    </label>
  );
}

function InsertBarSlot({
  colFiles,
  dragId,
  hotCard,
  index,
}: {
  colFiles: V2File[];
  dragId: string | null;
  hotCard: string | null;
  index: number;
}) {
  return (
    <SharedInsertBarSlot
      ids={colFiles.map((f) => f.id)}
      dragId={dragId}
      hot={hotCard}
      index={index}
      axis="x"
    />
  );
}

function placeBefore(fileId: string, controller: string, beforeId?: string) {
  return beforeId
    ? reorderFile({ fileId, beforeFileId: beforeId })
    : reorderFile({ fileId, controller });
}

export default function ControllerColumns({
  files,
  onClear,
  onDeleteFile,
  ...add
}: {
  files: V2File[];
  onClear: () => void;
  onDeleteFile: (fileId: string) => void;
} & SectionAddProps) {
  const dispatch = useDispatch();
  const [dragId, setDragId] = useState<string | null>(null);
  const [hotCol, setHotCol] = useState<string | null>(null);
  const [hotCard, setHotCard] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const gcodeFiles = files.filter((f) => f.kind === 'gcode');

  const clearDrag = () => {
    setDragId(null);
    setHotCol(null);
    setHotCard(null);
  };

  /** Dropping on the column itself sends the file to the bottom of it. */
  const onColDrop = (controller: string) => {
    if (dragId) {
      dispatch(updateFileController({ fileId: dragId, controller }));
      dispatch(reorderFile({ fileId: dragId, controller }));
    }
    clearDrag();
  };

  /** Dropping anywhere on a card takes that card's slot — no dead half. */
  const onCardDrop = (
    controller: string,
    colFiles: V2File[],
    index: number
  ) => {
    if (dragId && colFiles[index].id !== dragId) {
      dispatch(updateFileController({ fileId: dragId, controller }));
      dispatch(
        placeBefore(dragId, controller, dropBeforeId(colFiles, dragId, index))
      );
    }
    clearDrag();
  };

  return (
    <SectionPanel
      icon={Cpu}
      title="G-code — confirm each file's controller"
      help="We read each file header to tell Masso, BuildBotics and Redline apart. Drag a card to another column if we guessed wrong."
      count={gcodeFiles.length}
      headerRight={<OrderGate count={gcodeFiles.length} />}
      clearLabel="Remove all G-code"
      onClear={onClear}
      addLabel="Add G-code"
      {...add}
    >
      {/* The columns are for re-assigning files, not adding them — with nothing
          to drag they are three empty boxes. */}
      {gcodeFiles.length === 0 && <NoGcodeArt />}

      {gcodeFiles.length > 0 && (
        <>
          <p className="text-sm text-muted-foreground mb-4 max-w-[640px]">
            We grouped your G-code by the controller we detected —{' '}
            <strong>drag a card</strong> to move it to the right column.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {CONTROLLER_DEFS.map((def) => {
              const colFiles = gcodeFiles.filter(
                (f) => f.controller === def.key
              );
              return (
                <div
                  key={def.key}
                  data-testid={`v2-controller-col-${def.key}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    // eslint-disable-next-line no-param-reassign
                    e.dataTransfer.dropEffect = 'move';
                    setHotCol(def.key);
                    setHotCard(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    onColDrop(def.key);
                  }}
                  className={cn(
                    'bg-muted/30 border rounded-xl p-2.5 transition-[background-color,border-color,box-shadow] duration-fast min-h-[170px]',
                    hotCol === def.key && 'border-primary bg-accent shadow-sm'
                  )}
                >
                  <div className="px-1.5 pt-1 pb-2.5 mb-2.5 border-b">
                    <div className="flex items-center gap-2">
                      <ControllerLogo name={def.key} />
                      <span className="text-[15px] font-semibold text-heading">
                        {def.key}
                      </span>
                      <span className="grow" />
                      <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold flex items-center justify-center">
                        {colFiles.length}
                      </span>
                    </div>
                    <div className="text-[11px] font-tertiary text-muted-foreground mt-1 pl-[18px]">
                      {def.sub}
                    </div>
                  </div>
                  {/* Two cards across per column — three columns of one card each
                  stretched every card to a third of the page. */}
                  <div className="grid grid-cols-2 gap-2 min-h-[60px]">
                    {colFiles.map((f, i) => (
                      // stopPropagation, or the column's handlers run straight after
                      // these and overwrite them: the card highlight would be
                      // replaced by the column's, and the drop would be handled
                      // twice — once as "insert before this card", once as "append
                      // to the column".
                      <div
                        key={f.id}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // eslint-disable-next-line no-param-reassign
                          e.dataTransfer.dropEffect = 'move';
                          setHotCard(f.id);
                          setHotCol(null);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onCardDrop(def.key, colFiles, i);
                        }}
                        // A drag abandoned outside any column still ends — without
                        // this the highlight and the held file id stay stuck.
                        onDragEnd={clearDrag}
                        // 40ms apart, capped at 12, and the index restarts per
                        // column so the three fill in parallel. Decorative only —
                        // the card is draggable from the first frame regardless.
                        className="relative animate-in fade-in duration-base ease-out-strong fill-mode-both motion-reduce:animate-none"
                        style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
                      >
                        <InsertBarSlot
                          colFiles={colFiles}
                          dragId={dragId}
                          hotCard={hotCard}
                          index={i}
                        />
                        <FileCard
                          file={f}
                          index={i + 1}
                          onDragStart={setDragId}
                          isHot={false}
                          onOpen={() => setOpenId(f.id)}
                          onDelete={() => onDeleteFile(f.id)}
                          readOnlyName
                        />
                      </div>
                    ))}
                    {colFiles.length === 0 && (
                      <div className="col-span-2 py-5 text-center text-[10px] font-tertiary text-muted-foreground border border-dashed rounded-lg">
                        drop files here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <GcodeDetailModal
        file={gcodeFiles.find((f) => f.id === openId) ?? null}
        onClose={() => setOpenId(null)}
      />
    </SectionPanel>
  );
}
